// Testing a model on pictures from your own computer (Models ▸ Test…; customer feedback, Oliver). The server answers
// once at a low threshold (backend/app/api/model_test.py); the page draws what clears the slider, so moving it never
// runs the model again. Pure helpers, kept free of React so they can be tested.
import type { TestDetection, TestResult } from "../api/ml";
import type { LabelClass } from "../api/types";

/** backend/app/api/model_test.py MAX_FILES: pictures per request. */
export const TEST_BATCH = 20;
/** Pictures on the panel at once; the server also caps a person's hour on a metered server. */
export const TEST_MAX_PICTURES = 100;
/** The slider, in percent: the server's floor (5%) up to 95%; 25% to start, a common choice for judging a detector. */
export const THRESHOLD = { min: 5, max: 95, step: 5, initial: 25 };

export const shown = (r: TestResult, threshold: number) => r.detections.filter((d) => d.confidence >= threshold / 100);
export const pct = (confidence: number) => `${Math.round(confidence * 100)}%`;
/** "Carton 91%", the label drawn on the picture. */
export const label = (d: TestDetection) => `${d.class || "No class"} ${pct(d.confidence)}`;

/** How many of each class, most first, then by name. */
export function countByClass(dets: TestDetection[]): [string, number][] {
  const n = new Map<string, number>();
  for (const d of dets) n.set(d.class || "No class", (n.get(d.class || "No class") ?? 0) + 1);
  return [...n].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}
const list = (counts: [string, number][]) => counts.map(([c, k]) => `${c} ${k}`).join(", ");

/** Under one picture: "2 found: Carton 2", "Nothing found at 25% or more", or why it could not be read. */
export function pictureSummary(r: TestResult, threshold: number): string {
  if (r.error) return r.error;
  const d = shown(r, threshold);
  return d.length ? `${d.length} found: ${list(countByClass(d))}` : `Nothing found at ${threshold}% or more`;
}

/** Over the run: "3 pictures · 5 objects at 25% or more: Carton 4, Screw 1 · nothing on 1". */
export function runSummary(results: TestResult[], threshold: number): string {
  const ok = results.filter((r) => !r.error);
  const all = ok.flatMap((r) => shown(r, threshold));
  const empty = ok.filter((r) => !shown(r, threshold).length).length;
  const failed = results.length - ok.length;
  const parts = [`${results.length} picture${results.length === 1 ? "" : "s"}`,
    all.length ? `${all.length} object${all.length === 1 ? "" : "s"} at ${threshold}% or more: ${list(countByClass(all))}` : `nothing found at ${threshold}% or more`];
  if (all.length && empty) parts.push(`nothing on ${empty}`);
  if (failed) parts.push(`${failed} could not be read`);
  return parts.join(" · ");
}

// For a class the project does not have (a model imported with other names): a colour of its own, the same each time.
const SPARE = ["#E5484D", "#4CAF7D", "#E5B454", "#2E86F5", "#A15DE3", "#16A3A3", "#F0730E", "#D6409F"];
/** The project class's own colour, as the annotation editor draws it. */
export function colorFor(d: TestDetection, classes: LabelClass[]): string {
  const c = classes.find((x) => x.id === d.class_id) ?? classes.find((x) => x.name === d.class);
  if (c) return c.color_hex;
  let h = 0;
  for (const ch of d.class) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return SPARE[h % SPARE.length];
}

export type Geometry = { kind: "rect"; x: number; y: number; w: number; h: number } | { kind: "polygon"; points: [number, number][] };
/** What to draw, in picture pixels. */
export function geometry(d: TestDetection): Geometry {
  const c = d.coordinates;
  if ("points" in c) return { kind: "polygon", points: c.points };
  return { kind: "rect", x: Math.min(c.x1, c.x2), y: Math.min(c.y1, c.y2), w: Math.abs(c.x2 - c.x1), h: Math.abs(c.y2 - c.y1) };
}

/** Where a shape's label hangs: a box's top-left corner; a polygon's highest point (the leftmost, if several). */
export function anchor(d: TestDetection): [number, number] {
  const g = geometry(d);
  if (g.kind === "rect") return [g.x, g.y];
  return g.points.reduce((best, p) => (p[1] < best[1] || (p[1] === best[1] && p[0] < best[0]) ? p : best), g.points[0]);
}
